/**
*
*   The Cart Page
*       - A left/right content block + image or video
*
**/
'use client';

import Config from '@/config';
import Image from '@/components/image';
import Button from '@/components/button';
import Preloader from '@/components/preloader';
import Affirm from '@/libraries/affirm';
import Link from 'next/link';

import { QuickCartContext } from '@/context/quick-cart';
import { CartContext } from '@/context/cart';
import { useTranslations, useLocale } from 'next-intl';
import { CustomerContext } from '@/context/customer';
import { useState, useContext, useEffect, Fragment } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

//Build the Element
export default function CartPage({ children , product , ...props }){

	//Translations
	const t = useTranslations();

	//Locale
	const locale = useLocale();

	//Cart Context
	const { Cart } = useContext(CartContext);

	//QuickCart Context
	const { QuickCart } = useContext(QuickCartContext);

	//Leverage the QuickCart state for loading
	const loading = Cart.loading;

	//Get the Customer
	const { Customer } = useContext(CustomerContext);

	//Form Settings
	const { control, register, reset, setError, setValue, formState: { errors, isSubmitting, isSubmitSuccessful }, handleSubmit } = useForm();

	//The Cart Line Item Data
	const lines = ( Cart.get( cart => cart.lines ) || [] );

	//Affirm Offers
	const [ payments , setPayments ] = useState('');

	//Gift Message
	const [ message , showMessage ] = useState(false);

	//Load the Upsell Products
	useEffect(()=>{

		//Get the Affirm Value
		(async () => {

			const offer = await Affirm.get({
				amount:     Cart.get( cart => cart.cost.subtotalAmount.amount ),
				pageType:   'cart',
				locale:     locale
			});

			setPayments( offer ? offer : '' );

		})();

	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[ Cart.data ]);

	//Remove expired outdated birthday gifts on load
	useEffect(()=>{

		//Only if we have items in the cart
		if( Cart.data?.lineItems?.length > 0 ){

			//Only run for applicable customers
			if( Customer.is.loggedin() && !Customer.is.wholesale() ) Cart.get( cart => {

				//Get the Gift
				const gift = cart.free.samples.filter( sample => sample.type == 'birthday' )[0];

				//If a gift has been received, or the last month has past
				if( gift?.added && Customer.get( customer => !customer.birthday.gifted || !customer.birthday.inLastMonth ) ){

					//Get the line item;
					const line = cart.lineItems.find( item => item?.variant?.id == gift.samples?.[0]?.id );

					//Remove the Gift
					if( line ) Cart.remove( line );

				}

			});

		}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	},[ Cart.loading ]);

	//Prepare any sample offerings
	const samples = ( Cart.get( cart =>
		cart.free.samples.filter( sample => {

			//Standard Gift Validator
			if( sample.requirement ) return !sample.added;

			//Birthday Gift Validator
			if( sample.type == 'birthday' ) return (
				!sample.added &&
				Customer.is.loggedin() && !Customer.is.wholesale() &&
				Customer.get( customer => customer.birthday.gifted && customer.birthday.inLastMonth )
			);

		}).map( item  => {
			return {
				id: item.samples[0].id,
				gift: true,
				classes: 'demo',
				requirement: item.requirement,
				button: item.button,
				quantity: 1,
				product: {
					...item,
					variant: item.samples[0]
				}
			};
		})
	) || [] );

	//The Next Available Sample
	const sample = Cart.get( cart => cart.free.samples.filter( sample => !sample.added )[0] );

	//Return
	return (
		<form id="cart" noValidate className="container">
			<div className="view" data-responsive="desktop">
				<Preloader status={ loading } sticky />
				<table>
					<thead>
						<tr>
							<th className="thumbnail"></th>
							<th className="product">{ t('Pages.Cart.labels.product') }</th>
							<th className="price">{ t('Pages.Cart.labels.price') }</th>
							<th className="quantity">{ t('Pages.Cart.labels.quantity') }</th>
							<th className="total">{ t('Pages.Cart.labels.total') }</th>
							<th className="action"></th>
						</tr>
					</thead>
					<tbody data-cart="desktop">
						{ lines.length == 0 && (
								<tr className="none">
									<td colSpan="6">{
										t('Components.Cart.empty')
									}</td>
								</tr>
						)}
						{ lines.length > 0 && (
							[ ...samples , ...lines ].map( item => (
								<Fragment key={ `cart-${  item.product.variant.id }` }>
									<tr className={ item.classes || '' }>

										{/* Featured Image */}
										<td className="thumbnail">
											<Link href={ `/products/${  item.product.handle }` }>
												<Image src={ item.product.featuredImage } alt="" width="160" height="160" />
											</Link>
										</td>
										<td className="details">

											{/* Details */}
											<div className="product">

												<Link href={ `/products/${  item.product.handle }` } className="title">
													{ item.product.title }
												</Link>

												<p className="type">
													<span>{ item.product.productType }</span>
												</p>

												<p className="variant">{
													item.product.variant.title.replace(
														/((\/ ?)?default( title)?)|\(100% off\)/gi
													,'')
												}</p>

											</div>

											{/* Quantity */}
											<div className="quantity">
											</div>

										</td>
										<td className="price">
											{ item.redemption && (
												<>
													<sup className="was">{ Cart.money( item.product.variant.price.amount ) }</sup>
													{t('Components.Cart.points',{
														points: item.product.variant.price.amount * Config.points
													})}
												</>
											)}
											{ !item.redemption && (
												<>
													{ item.product.variant.compareAtPrice?.amount > item.product.variant.price.amount && (
														<sup className="was">{ Cart.money( item.product.variant.compareAtPrice.amount ) }</sup>
													)}
													{ Cart.money( item.product.variant.price.amount * ( item.quantity || 1 ) ) }
												</>
											)}
										</td>
										<td className="quantity">
											<input
												aria-label={ t('Components.Cart.quantity') }
												type="number"
												min="1"
												readOnly={ item.gift || false }
												{...register( item.id , {
													value: item.quantity,
													onChange: (e) => {

														//Remove the item if we have no quantity
														if( e.target.value <= 0 )
															return Cart.remove( item );

														//If it's a lower value, just accept
														if( item.quantity > e.target.value )
															return Cart.update( item , e.target.value );

														//If it's not points, just continue
														if( !item.appliedDiscount )
															return Cart.update( item , e.target.value );

														//Get the difference
														const difference = e.target.value - item.quantity;

														//Get the customer points
														const points = Customer.get( customer => customer.rewards.points );

														//If we have enough points
														if( points > ( item.variant.price * Config.points * difference ) )
															return Cart.update( item , e.target.value );

														//Show the error
														setError( item.id , { type: 'custom' , message: t('Components.Cart.insufficient') });

														//Revert
														setValue( item.id , item.quantity);

													}
												})}
											/>
											<ErrorMessage errors={errors} name={ item.id } render={({ message })=>(<p className="error">{ message }</p>)} />
										</td>
										<td className="total">
											{ item.redemption && (
												<>
													<sup className="was">{ Cart.money( item.product.variant.price.amount * item.quantity ) }</sup>
													{t('Components.Cart.points',{
														points: item.product.variant.price.amount * Config.points * ( item.quantity || 1 )
													})}
												</>
											)}
											{ !item.redemption && (
												<>
													{ item.product.variant.compareAtPrice?.amount > item.product.variant.price.amount && (
														<sup className="was">{ Cart.money( item.product.variant.compareAtPrice.amount * item.quantity ) }</sup>
													)}
													{ Cart.money( item.product.variant.price.amount * ( item.quantity || 1 ) ) }
												</>
											)}
										</td>
										<td className="action">

											<div className="list">

												{/* Remove Item */}
												<a onClick={ () => Cart.remove(item) }>
													<i className="far fa-times-circle"></i>
													<span>{ t('Components.Cart.remove') }</span>
												</a>

											</div>

										</td>
									</tr>
									{ item.gift && (
										<tr className="notice">
											<td colSpan="6">
												<div>
													{ Cart.get( cart =>  !item.requirement || ( cart.cost.subtotalAmount.amount ) >= item.requirement ) ? (
														<Button onClick={ () => Cart.add( item.product , 1 ) }>{ t( item.button ) }</Button>
													) : (
														<>
															<div className="icon">
																<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
																	<path d="M365.1 32c-17.6 0-33.9 9-43.2 23.9l-45 72.1H288h80c26.5 0 48-21.5 48-48s-21.5-48-48-48h-2.9zm-130 96l-45-72.1C180.8 41 164.5 32 146.9 32H144c-26.5 0-48 21.5-48 48s21.5 48 48 48h80 11.1zM256 101l38.8-62c15.2-24.2 41.7-39 70.3-39H368c44.2 0 80 35.8 80 80c0 18-6 34.6-16 48h48 32v32 96 32H480V480v32H448 64 32V480 288H0V256 160 128H32 80C70 114.6 64 98 64 80C64 35.8 99.8 0 144 0h2.9c28.6 0 55.2 14.7 70.3 39L256 101zM144 160H32v96H64 240V160H224 144zm128 0v96H448h32V160H368 288 272zM240 288H64V480H240V288zm32 192H448V288H272V480z"/>
																</svg>
															</div>
															<p>
																{
																	t.rich('Components.Cart.gifts.remaining',{
																		...Config.i18n.html,
																		total: (chunk) => Cart.money( Cart.get( cart => ( item.requirement - cart.cost.subtotalAmount.amount ) ) )
																	})
																}
															</p>
														</>
													)}
												</div>
											</td>
										</tr>
									)}
								</Fragment>
							))
						)}
					</tbody>
				</table>
			</div>

			<div className="view" data-responsive="mobile">
				<Preloader status={ loading } sticky />
				<table>
					<tbody data-cart="mobile">
						{ lines.length == 0 && (
								<tr className="none">
									<td colSpan="3">{
										t('Components.Cart.empty')
									}</td>
								</tr>
						)}
						{lines.length > 0 && (
							[ ...samples , ...lines ].map( item => (
								<Fragment key={ `cart-${  item.product.variant.id }` }>
									<tr className={ item.classes || '' }>

										{/* Featured Image */}
										<td className="thumbnail">
											<Link href={ `/products/${  item.product.handle }` }>
												<Image src={ item.product.featuredImage } alt="" width="160" height="160" />
											</Link>
										</td>
										<td className="details">

											{/* Details */}
											<div className="product">

												<Link href={ `/products/${  item.product.handle }` } className="title">
													{ item.product.title }
												</Link>

												<p className="type">
													<span>{ item.product.productType }</span>
												</p>

												<p className="variant">{
													item.product.variant.title.replace(
														/((\/ ?)?default( title)?)|\(100% off\)/gi
													,'')
												}</p>

											</div>

											{/* Quantity */}
											<div className="quantity">
												<input
													aria-label={ t('Components.Cart.quantity') }
													type="number"
													min="1"
													readOnly={ item.gift || false }
													{...register( item.id , {
														value: item.quantity,
														onChange: (e) => {

															//Remove the item if we have no quantity
															if( e.target.value <= 0 )
																return Cart.remove( item );

															//If it's a lower value, just accept
															if( item.quantity > e.target.value )
																return Cart.update( item , e.target.value );

															//If it's not points, just continue
															if( !item.appliedDiscount )
																return Cart.update( item , e.target.value );

															//Get the difference
															const difference = e.target.value - item.quantity;

															//Get the customer points
															const points = Customer.get( customer => customer.rewards.points );

															//If we have enough points
															if( points > ( item.variant.price * Config.points * difference ) )
																return Cart.update( item , e.target.value );

															//Show the error
															setError( item.id , { type: 'custom' , message: t('Components.Cart.insufficient') });

															//Revert
															setValue( item.id , item.quantity);

														}
													})}
												/>
												<ErrorMessage errors={errors} name={ item.id } render={({ message })=>(<p className="error">{ message }</p>)} />
											</div>

										</td>
										<td className="action">

											{/* Cart Total */ }
											<div data-label={ t('Components.Cart.total') } className="total">
												{ item.redemption && (
													<>
														<sup className="was">{ Cart.money( item.product.variant.price.amount * item.quantity ) }</sup>
														{t('Components.Cart.points',{
															points: item.product.variant.price.amount * Config.points * ( item.quantity || 1 )
														})}
													</>
												)}
												{ !item.redemption && (
													<>
														{ item.product.variant.compareAtPrice?.amount > item.product.variant.price.amount && (
															<sup className="was">{ Cart.money( item.product.variant.compareAtPrice.amount * item.quantity ) }</sup>
														)}
														{ Cart.money( item.product.variant.price.amount * ( item.quantity || 1 ) ) }
													</>
												)}
											</div>

											<div className="list">

												{/* Remove Item */}
												<a onClick={ () => Cart.remove(item) }>
													<i className="far fa-times-circle"></i>
													<span>{ t('Components.Cart.remove') }</span>
												</a>

											</div>

										</td>
									</tr>
									{ item.gift && (
										<tr className="notice">
											<td colSpan="6">
												<div>
													{ Cart.get( cart =>  !item.requirement || ( cart.cost.subtotalAmount.amount ) >= item.requirement ) ? (
														<Button onClick={ () => Cart.add( item.product , 1 ) }>{ t( item.button ) }</Button>
													) : (
														<>
															<div className="icon">
																<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
																	<path d="M365.1 32c-17.6 0-33.9 9-43.2 23.9l-45 72.1H288h80c26.5 0 48-21.5 48-48s-21.5-48-48-48h-2.9zm-130 96l-45-72.1C180.8 41 164.5 32 146.9 32H144c-26.5 0-48 21.5-48 48s21.5 48 48 48h80 11.1zM256 101l38.8-62c15.2-24.2 41.7-39 70.3-39H368c44.2 0 80 35.8 80 80c0 18-6 34.6-16 48h48 32v32 96 32H480V480v32H448 64 32V480 288H0V256 160 128H32 80C70 114.6 64 98 64 80C64 35.8 99.8 0 144 0h2.9c28.6 0 55.2 14.7 70.3 39L256 101zM144 160H32v96H64 240V160H224 144zm128 0v96H448h32V160H368 288 272zM240 288H64V480H240V288zm32 192H448V288H272V480z"/>
																</svg>
															</div>
															<p>
																{
																	t.rich('Components.Cart.gifts.remaining',{
																		...Config.i18n.html,
																		total: (chunk) => Cart.money( Cart.get( cart => ( item.requirement - cart.cost.subtotalAmount.amount ) ) )
																	})
																}
															</p>
														</>
													)}
												</div>
											</td>
										</tr>
									)}
								</Fragment>
							))
						)}
					</tbody>
				</table>
			</div>


			<div className="subtotal">

				<div className="box">

					<p className="title">
						{ t('Pages.Cart.labels.subtotal') }
					</p>
					{/* Incentives */}
					<div className="incentive">
						{
							Cart.get( cart => cart.cost.subtotalAmount.amount < cart.free.shipping ) ? (
								<>
									<p>{ t.rich('Components.Cart.remaining',{
										...Config.i18n.html,
										total:  (chunks) => (
											<strong>
												<Preloader status={ loading } mini />
												{ !loading && Cart.money( Cart.get( cart => ( cart.free.shipping - cart.cost.subtotalAmount.amount ) ) ) }
											</strong>
										)
									})}</p>
								</>
							) : sample && Cart.get( cart => sample.requirement > cart.cost.subtotalAmount.amount ) ? (
								<>
									<p>{ t.rich('Components.Cart.gifts.remaining',{
										...Config.i18n.html,
										total:  (chunks) => (
											<strong>
												<Preloader status={ loading } mini />
												{ !loading && Cart.money( Cart.get( cart => ( sample.requirement - cart.cost.subtotalAmount.amount ) ) ) }
											</strong>
										)
									})}</p>
								</>
							) : (
								<>
									<Preloader status={ loading } mini />
									{ !loading && ( <p>{ t('Pages.Cart.qualified') }</p> )}
								</>
							)
						}
					</div>

					{/* Checkout Button */}
					<Button disabled={ loading || isSubmitting || lines.length == 0 } button type="submit">
						<span className="price">
							{
								Cart.get( cart => (
									cart.original_total_price > cart.total_price && (
										<strike className="was">
											<Preloader status={ loading } mini />
											{ !loading && cart.original_total_price }
										</strike>
									)
								))
							}
							<span className="current">
								<Preloader status={ loading } mini />
								{ !loading && Cart.money( ( Cart.get( cart => cart.cost.subtotalAmount.amount ) || 0 ) ) }
							</span> - </span>{ t('Components.Cart.checkout') }
					</Button>

					{/* Affirm Offer */}
					<p className="payments">
						<Preloader status={ loading } mini />
						{ !loading && (
							<span dangerouslySetInnerHTML={{
								__html: payments
							}} />
						)}
					</p>


					{/* Calculations Note */}
					<p className="info">{
						t.rich('Components.Cart.info',{
							...Config.i18n.html,
							em: (chunk) => (<em>{chunk}</em>)
						})
					}</p>

				</div>

				{/* */}
				<div className="gift">

					<a onClick={ () => showMessage( !message ) } className="gift-button">{
						t(`Pages.Cart.message.${ !message ? 'show' : 'hide' }`)
					}</a>

					{ message && (
						<div className="message" data-gift-area>

							<div className="head">
								<h6>{ t('Pages.Cart.message.add') }</h6>
							</div>
							<textarea {...register( 'attributes.note' )}></textarea>

						</div>
					)}
				</div>

			</div>

		</form>

	);

}